import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import { inputStyles } from 'assets/components/inputs/Input';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { defaultInputDateTimeFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { isEmail } from 'assets/utils/parsersAndValidation/Validators';
import Theme from 'assets/themes/Theme.Common';
import dayjs from 'dayjs';
import { isEmpty, isNil, set, sumBy } from 'lodash';
import PaymentType from 'models/enums/PaymentType';
import { useContractRefundPaymentItemProvider } from '../Finance.ContractRefundPaymentItem.Hooks';

export default function CreateContractRefundPaymentFromItems() {
  const { lang, translate } = getLocales();
  const { userId } = useAuthStore();
  const { pageRouter, createContractRefundPayment, selectedItems, permissions, payment, postingAccountListApi } =
    useContractRefundPaymentItemProvider();

  if (isEmpty(selectedItems) || !permissions.CREATE_CONTRACTREFUNDPAYMENT) {
    return null;
  }

  return (
    <ModalForm<Api.Finance.ContractRefundPayment.Save.IRequest>
      subcomponents={ThemeModalFormDefault}
      title={lang.createPayment}
      default={{
        ownerId: userId,
        transactionDateUtc: dayjs().format(defaultServerDateTimeFormat),
        paymentDateUtc: dayjs().format(defaultServerDateTimeFormat),
        amountPaid: payment?.amountPaid,
        gstHstAmountPaid: payment?.gstHstAmountPaid,
        provincialSalesTaxAmountPaid: payment?.provincialSalesTaxAmountPaid,
        paymentMethodCode: payment?.paymentMethodCode,
        paymentEmailAddress: payment?.isETransferOrUsBank ? payment?.payeeEmail : undefined,
        remittanceEmailAddress: payment?.payeeEmail,
        contractRefundPaymentPayeeId: payment?.contractRefundPaymentPayeeId ?? payment?.payee?.id,
        createdAtLocal: dayjs().format(defaultServerDateTimeFormat),
      }}
      onCancel={pageRouter.updateParams}
      onSubmit={async (data) => {
        await createContractRefundPayment(data);
      }}
      validation={(data, errors) => {
        if (!data.transactionDateUtc) set(errors, 'transactionDateUtc', lang.mustNotBeEmpty);
        if (!data.amountPaid) set(errors, 'amountPaid', lang.mustNotBeEmpty);
        if (isNil(data.gstHstAmountPaid)) set(errors, 'gstHstAmountPaid', lang.mustNotBeEmpty);
        if (isNil(data.provincialSalesTaxAmountPaid)) set(errors, 'provincialSalesTaxAmountPaid', lang.mustNotBeEmpty);
        if (!data.paymentMethodCode) set(errors, 'paymentMethodCode', lang.mustNotBeEmpty);
        if (!data.referenceNumber) set(errors, 'referenceNumber', lang.mustNotBeEmpty);
        if (
          data.ledgerAccountId &&
          postingAccountListApi?.payload?.data?.find((it) => it.id === data.ledgerAccountId)?.currency !==
            payment.currency
        ) {
          set(errors, 'ledgerAccountId', lang.mustNotBeEmpty);
        }
        if (!data.remittanceEmailAddress) set(errors, 'remittanceEmailAddress', lang.mustNotBeEmpty);
        if (data.remittanceEmailAddress && !isEmail(data.remittanceEmailAddress))
          set(errors, 'remittanceEmailAddress', lang.invalidEmailFormatRequires);

        const isETransferOrUsBank =
          data.paymentMethodCode === PaymentType.usBank || data.paymentMethodCode === PaymentType.ETransfer;
        if (isETransferOrUsBank && !data.paymentEmailAddress) set(errors, 'paymentEmailAddress', lang.mustNotBeEmpty);
        if (isETransferOrUsBank && data.paymentEmailAddress && !isEmail(data.paymentEmailAddress))
          set(errors, 'paymentEmailAddress', lang.invalidEmailFormatRequires);
      }}
      render={({ form }) => (
        <>
          <FormWrapper>
            <h3>{lang.info}</h3>
            <div>
              {lang.contractRefund}: <b>{payment?.contractRefundId ? `RQ-${payment.contractRefundId}` : 'N/A'}</b>
            </div>
            <div>
              {lang.contractNumber}: <b>{selectedItems[0]?.appNumber ?? 'N/A'}</b>
            </div>
            <div>
              {lang.paymentMethod}:{' '}
              <b>{translate(Object.values(PaymentType).find((it) => it === payment?.paymentMethodCode))}</b>
            </div>

            <h3>{lang.jobs}</h3>
            {selectedItems.map((contractRefundPaymentItem) => (
              <div key={contractRefundPaymentItem.id}>
                <div>
                  {lang.jobNumber}: <b>{contractRefundPaymentItem.jobId}</b>
                </div>
                <div>
                  {lang.total}:{' '}
                  <b>
                    <FormatValue value={`${(contractRefundPaymentItem.totalAmount || 0).toFixed(2)}$`} />
                  </b>
                </div>
                <br />
              </div>
            ))}
            <div style={{ fontSize: Theme.FontSize.XL }}>
              {lang.totalSum}:{' '}
              <b>
                <FormatValue value={`${sumBy(selectedItems, (it) => Number(it.totalAmount) || 0).toFixed(2)}$`} />
              </b>
            </div>

            <h3>{lang.payee}</h3>
            <div>
              {lang.payeeName}: <b>{payment?.payee?.displayInfo?.title ?? 'N/A'}</b>
            </div>
            <div>
              {lang.payeeEmail}: <b>{payment?.payeeEmail ?? 'N/A'}</b>
            </div>
            <div>
              {lang.address}: <b>{payment?.payee?.fullAddress ?? 'N/A'}</b>
            </div>
            <div>
              {lang.city}: <b>{payment?.payee?.addressDetails?.city ?? 'N/A'}</b>
            </div>
            <div>
              {lang.province}: <b>{payment?.payee?.addressDetails?.province ?? 'N/A'}</b>
            </div>
            <div>
              {lang.postalCode}: <b>{payment?.payee?.addressDetails?.postalCode ?? 'N/A'}</b>
            </div>
            <hr />
            <br />

            <SelectInput
              name="ledgerAccountId"
              label={lang.postingAccounts}
              data={postingAccountListApi?.payload?.data
                ?.filter((item) => item.currency === payment.currency)
                ?.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
              value={form.data?.ledgerAccountId}
              error={form.errors?.ledgerAccountId}
              onChange={form.update}
              icon={<Icon class="fas-cloud" />}
            />
            <DateInput
              dateTimeFormat={defaultInputDateTimeFormat}
              name="transactionDateUtc"
              value={form.data?.transactionDateUtc}
              error={form.errors?.transactionDateUtc}
              label={lang.transactionDate}
              onChange={form.update}
              icon={<Icon class="fas-calendar-alt" />}
            />
            <h3 style={{ marginTop: 0 }}>{lang.payment}</h3>
            <TextInput
              name="referenceNumber"
              label={lang.referenceNumber}
              value={form.data.referenceNumber}
              error={form.errors.referenceNumber}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
            <TextInput
              name="amountPaid"
              label={lang.paidAmount}
              value={payment?.amountPaid?.toFixed(2)}
              error={form.errors.amountPaid}
              onChange={form.update}
              disabled
              icon={<Icon class="fas-dollar-sign" />}
            />
            <TextInput
              name="gstHstAmountPaid"
              label={lang.gstHstPaid}
              value={payment?.gstHstAmountPaid?.toFixed(2)}
              error={form.errors.gstHstAmountPaid}
              onChange={form.update}
              disabled
              icon={<Icon class="fas-dollar-sign" />}
            />
            <TextInput
              name="provincialSalesTaxAmountPaid"
              label={lang.pstPaid}
              value={payment?.provincialSalesTaxAmountPaid?.toFixed(2)}
              error={form.errors.provincialSalesTaxAmountPaid}
              onChange={form.update}
              disabled
              icon={<Icon class="fas-dollar-sign" />}
            />
            {form.data?.paymentMethodCode === PaymentType.eft && (
              <>
                <TextInput
                  name="bankTransit"
                  label={lang.bankTransit}
                  value={form.data.bankTransit}
                  error={form.errors.bankTransit}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="bankBranch"
                  label={lang.bankBranch}
                  value={form.data.bankBranch}
                  error={form.errors.bankBranch}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="bankAccount"
                  label={lang.bankAccount}
                  value={form.data.bankAccount}
                  error={form.errors.bankAccount}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="remittanceEmailAddress"
                  label={lang.remittanceEmailAddress}
                  value={form.data.remittanceEmailAddress}
                  error={form.errors.remittanceEmailAddress}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
              </>
            )}
            {form.data?.paymentMethodCode === PaymentType.cheque && (
              <>
                <TextInput
                  name="chequeNumber"
                  label={lang.chequeNumber}
                  value={form.data.chequeNumber}
                  error={form.errors.chequeNumber}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="remittanceEmailAddress"
                  label={lang.remittanceEmailAddress}
                  value={form.data.remittanceEmailAddress}
                  error={form.errors.remittanceEmailAddress}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
              </>
            )}
            {form.data?.paymentMethodCode === PaymentType.ETransfer && (
              <>
                <TextInput
                  name="transactionNumber"
                  label={lang.paymentTransactionNumber}
                  value={form.data.transactionNumber}
                  error={form.errors.transactionNumber}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="paymentEmailAddress"
                  label={lang.paymentEmailAddress}
                  value={form.data.paymentEmailAddress}
                  error={form.errors.paymentEmailAddress}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="remittanceEmailAddress"
                  label={lang.remittanceEmailAddress}
                  value={form.data.remittanceEmailAddress}
                  error={form.errors.remittanceEmailAddress}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <CheckInput
                  name="isAutoDepositEnabled"
                  label={lang.autoDepositEnabled}
                  class={inputStyles.check.switch}
                  value={form.data.isAutoDepositEnabled}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data.isAutoDepositEnabled ? lang.disable : lang.enable }}
                />
                <TextInput
                  name="eTransferQuestion"
                  label={lang.eTransferQuestion}
                  value={form.data.eTransferQuestion}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                {!form.data.isAutoDepositEnabled && (
                  <TextInput
                    name="eTransferAnswer"
                    label={lang.eTransferAnswer}
                    value={form.data.eTransferAnswer}
                    error={form.errors.eTransferAnswer}
                    onChange={form.update}
                    icon={<Icon class="if-quill-pen" />}
                  />
                )}
              </>
            )}
            {form.data?.paymentMethodCode === PaymentType.usBank && (
              <>
                <TextInput
                  name="paymentEmailAddress"
                  label={lang.paymentEmailAddress}
                  value={form.data.paymentEmailAddress}
                  error={form.errors.paymentEmailAddress}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="remittanceEmailAddress"
                  label={lang.remittanceEmailAddress}
                  value={form.data.remittanceEmailAddress}
                  error={form.errors.remittanceEmailAddress}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
              </>
            )}
            <h3 style={{ marginTop: 0 }}>{lang.comments}</h3>
            <MultilineTextInput
              name="explanation"
              label={lang.remarks}
              value={form.data.explanation}
              error={form.errors.explanation}
              onChange={form.update}
              icon={<Icon class="if-quill-pen" />}
            />
          </FormWrapper>
        </>
      )}
    />
  );
}
